import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleDateTime } from "@/utils/formatUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Card, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import { useFilterFinancialMediations } from "../../../../common/api/queries/financialMediation";
import ActionTextIcon from "../../../../common/components/icons/ActionTextIcon";
import Ellipsis from "../../../../common/components/views/Ellipsis";
import { TableSizes } from "../../../../common/constants";
import { ClientTypeTagWithName } from "../../../client/components/tags/ClientTypeTagWithName";
import { FINANCIAL_MEDIATION_ROUTE_PATHS } from "../../paths";
import { FinancialMediationFilterPageRequest, FinancialMediationList } from "../../types";
import FinancialMediationStatusTag from "../FinancialMediationStatusTag";
import FinancialMediationVersionTag from "../FinancialMediationVersionTag";

interface Props {
  searchParams: FinancialMediationFilterPageRequest;
}

export const FinancialMediationTableView = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { data: financeMediations } = useFilterFinancialMediations(searchParams);

  const columns: ColumnsType<FinancialMediationList> = [
    {
      key: "createdAt",
      title: t("common.createdAt"),
      width: 150,
      render: (_, record) => formatLocaleDateTime(record.createdAt),
    },
    {
      key: "updatedAt",
      title: t("common.updatedAt"),
      width: 150,
      render: (_, record) => formatLocaleDateTime(record.updatedAt),
    },
    {
      key: "sector",
      title: t("product.enums.financialSector._label"),
      width: 140,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{t("product.enums.financialSector." + record.sector)}</Ellipsis>,
    },
    {
      key: "policyHolder",
      title: t("financialMediation.attrs.policyHolder"),
      width: 205,
      render: (_, record) => <ClientTypeTagWithName client={record.policyHolder} ellipsis showLink />,
    },
    {
      key: "secondaryClient",
      title: t("financialMediation.attrs.secondaryClient"),
      width: 205,
      render: (_, record) =>
        record.secondaryClient && <ClientTypeTagWithName client={record.secondaryClient} ellipsis showLink />,
    },
    {
      key: "version",
      title: t("financialMediation.filter.version"),
      align: "center",
      width: 80,
      render: (_, record) => <FinancialMediationVersionTag version={record.version} style={{ marginRight: 0 }} />,
    },
    {
      key: "finished",
      title: t("financialMediation.filter.status"),
      align: "center",
      width: 130,
      render: (_, record) => <FinancialMediationStatusTag status={record.status} style={{ marginRight: 0 }} />,
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 90,
      render: (_, record) => (
        <ActionTextIcon
          path={generatePath(FINANCIAL_MEDIATION_ROUTE_PATHS.detail.to, { id: record.id })}
          icon="eye"
          color="blue"
          text={t("common.show")}
        />
      ),
    },
  ];

  return (
    <Card className="card-box">
      <Table<FinancialMediationList>
        {...tableStandardProps()}
        columns={columns}
        scroll={{ x: TableSizes.LARGE }}
        dataSource={financeMediations?.pageData}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(financeMediations?.pageIndex) + 1,
          pageSize: financeMediations?.pageSize,
          total: financeMediations?.totalElementsCount,
          onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
        }}
      />
    </Card>
  );
};
